import { FC, useCallback } from 'react';

import Button from 'components/_core/Button';
import SunshineModal, {
  SunshineModalProps,
} from 'components/_core/SunshineModal';
import Text from 'components/_core/Text';
import useToast from 'components/ToastProvider/useToast';

import useDeleteCatalogItemMutation from './hooks/useDeleteCatalogItemMutation';
import locales from './locales';

interface DeleteModalProps extends Omit<SunshineModalProps, 'children'> {
  catalogItemId: string;
}

const DeleteModal: FC<DeleteModalProps> = ({
  catalogItemId,
  isOpen,
  onClose,
  ...modalProps
}) => {
  const { showToast } = useToast();
  const [deleteCatalogItem, { loading }] = useDeleteCatalogItemMutation();

  const onDelete = useCallback(async () => {
    const { errors } = await deleteCatalogItem({
      invoiceCatalogItemId: catalogItemId,
    });

    if (errors) {
      showToast({ message: locales.errorMessage, type: 'error' });
      return;
    }

    showToast({ message: locales.successMessage, type: 'success' });
    onClose();
  }, [catalogItemId, deleteCatalogItem, onClose, showToast]);

  return (
    <SunshineModal {...modalProps} isOpen={isOpen} onClose={onClose}>
      <SunshineModal.Header />

      <SunshineModal.Body>
        <SunshineModal.Title>{locales.title}</SunshineModal.Title>

        <Text>{locales.subtitle}</Text>
      </SunshineModal.Body>

      <SunshineModal.Footer>
        <Button
          icon="arrow-left"
          iconPosition="left"
          onClick={onClose}
          variant="inline-primary"
        >
          {locales.back}
        </Button>

        <Button
          icon="trash"
          iconPosition="left"
          isLoading={loading}
          onClick={onDelete}
          variant="danger"
        >
          {locales.delete}
        </Button>
      </SunshineModal.Footer>
    </SunshineModal>
  );
};

export default DeleteModal;
